import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function VoucherForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        voucher_type_id: '',
        voucher_date: new Date().toISOString().split('T')[0],
        narration: '',
        voucher_entries_attributes: [
            { account_group_id: '', firm_bank_account_id: '', debit_amount: '0.00', credit_amount: '0.00', narration: '' },
            { account_group_id: '', firm_bank_account_id: '', debit_amount: '0.00', credit_amount: '0.00', narration: '' }
        ]
    });

    const [voucherTypes, setVoucherTypes] = useState([]);
    const [accountGroups, setAccountGroups] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadVoucherTypes();
        loadAccountGroups();
        loadBankAccounts();
        if (isEdit) {
            loadVoucher();
        }
    }, [id]);

    const loadVoucherTypes = async () => {
        try {
            const response = await accountingApi.getVoucherTypes();
            if (response.success) {
                const types = Array.isArray(response.data) ? response.data : (response.data?.voucher_types || response.data?.data || []);
                setVoucherTypes(types);
            } else {
                console.error('Error loading voucher types:', response.message);
            }
        } catch (error) {
            console.error('Error loading voucher types:', error);
        }
    };

    const loadAccountGroups = async () => {
        try {
            const response = await accountingApi.getAccountGroups();
            if (response.success) {
                const groups = Array.isArray(response.data) ? response.data : (response.data?.account_groups || response.data?.data || []);
                setAccountGroups(groups);
            } else {
                console.error('Error loading account groups:', response.message);
            }
        } catch (error) {
            console.error('Error loading account groups:', error);
        }
    };

    const loadBankAccounts = async () => {
        try {
            const response = await accountingApi.getBankAccounts();
            if (response.success) {
                const accounts = Array.isArray(response.data) ? response.data : (response.data?.bank_accounts || response.data?.data || []);
                setBankAccounts(accounts);
            } else {
                console.error('Error loading bank accounts:', response.message);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    const loadVoucher = async () => {
        try {
            const response = await accountingApi.getVoucher(id);
            if (response.success) {
                const voucher = response.data?.voucher || response.data;
                if (voucher && voucher.entries) {
                    setFormData({
                        voucher_type_id: voucher.voucher_type?.id || '',
                        voucher_date: voucher.voucher_date || '',
                        narration: voucher.narration || '',
                        voucher_entries_attributes: voucher.entries.map(entry => ({
                            id: entry.id,
                            account_group_id: entry.account_group?.id || '',
                            firm_bank_account_id: entry.firm_bank_account?.id || '',
                            debit_amount: entry.debit_amount || '0.00',
                            credit_amount: entry.credit_amount || '0.00',
                            narration: entry.narration || ''
                        }))
                    });
                }
            } else {
                setErrors([response.message || 'Failed to load voucher']);
            }
        } catch (error) {
            setErrors(['Failed to load voucher']);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleEntryChange = (index, field, value) => {
        setFormData(prev => {
            const entries = [...prev.voucher_entries_attributes];
            entries[index] = { ...entries[index], [field]: value };
            return { ...prev, voucher_entries_attributes: entries };
        });
    };

    const addEntry = () => {
        setFormData(prev => ({
            ...prev,
            voucher_entries_attributes: [
                ...prev.voucher_entries_attributes,
                { account_group_id: '', firm_bank_account_id: '', debit_amount: '0.00', credit_amount: '0.00', narration: '' }
            ]
        }));
    };

    const removeEntry = (index) => {
        setFormData(prev => {
            const entries = [...prev.voucher_entries_attributes];
            if (entries[index].id) {
                entries[index] = { ...entries[index], _destroy: true };
            } else {
                entries.splice(index, 1);
            }
            return { ...prev, voucher_entries_attributes: entries };
        });
    };

    const calculateTotals = () => {
        const entries = formData.voucher_entries_attributes.filter(e => !e._destroy);
        const totalDebit = entries.reduce((sum, e) => sum + parseFloat(e.debit_amount || 0), 0);
        const totalCredit = entries.reduce((sum, e) => sum + parseFloat(e.credit_amount || 0), 0);
        return { totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { balanced } = calculateTotals();
        if (!balanced) {
            setErrors(['Total debit and credit amounts must be equal']);
            return;
        }

        try {
            setSaving(true);
            setErrors([]);

            const response = isEdit
                ? await accountingApi.updateVoucher(id, formData)
                : await accountingApi.createVoucher(formData);

            if (response.success) {
                // Always redirect to vouchers list after create/update
                navigate('/accounting/vouchers');
            } else {
                setErrors(response.errors || [response.message || 'Failed to save voucher']);
            }
        } catch (error) {
            setErrors(['Failed to save voucher']);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    const { totalDebit, totalCredit, balanced } = calculateTotals();
    const visibleEntries = formData.voucher_entries_attributes.filter(e => !e._destroy);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting/vouchers" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">
                        {isEdit ? 'Edit Voucher' : 'New Voucher'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {errors.length > 0 && (
                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md">
                        <ul className="list-disc list-inside text-sm text-rose-700">
                            {errors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {!balanced && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md">
                        <p className="text-sm text-amber-700">
                            ⚠️ Voucher is not balanced. Total Debit: ₹{totalDebit.toFixed(2)}, Total Credit: ₹{totalCredit.toFixed(2)}
                        </p>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Voucher Type <span className="text-rose-500">*</span>
                            </label>
                            <select
                                name="voucher_type_id"
                                value={formData.voucher_type_id}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                            >
                                <option value="">Select Type</option>
                                {voucherTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name} ({type.voucher_code})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                Date <span className="text-rose-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="voucher_date"
                                value={formData.voucher_date}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">Narration</label>
                        <textarea
                            name="narration"
                            value={formData.narration}
                            onChange={handleChange}
                            rows="2"
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all resize-none"
                            placeholder="Brief description of the transaction..."
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="p-4 bg-slate-50 border-b border-slate-200">
                        <h3 className="font-bold text-slate-800">Voucher Entries</h3>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-100">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Account</th>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-600">Bank/Cash</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-600">Debit (₹)</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-slate-600">Credit (₹)</th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-slate-600">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleEntries.map((entry, index) => (
                                    <tr key={index} className="border-b border-slate-100">
                                        <td className="px-4 py-3">
                                            <select
                                                value={entry.account_group_id}
                                                onChange={(e) => handleEntryChange(index, 'account_group_id', e.target.value)}
                                                required
                                                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                            >
                                                <option value="">Select Account</option>
                                                {accountGroups.map(group => (
                                                    <option key={group.id} value={group.id}>{group.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            {(() => {
                                                const selectedGroup = accountGroups.find(g => g.id.toString() === entry.account_group_id?.toString());
                                                const isMainBankAccount = selectedGroup?.name === 'Main Bank Account';

                                                if (isMainBankAccount) {
                                                    return (
                                                        <select
                                                            value={entry.firm_bank_account_id || ''}
                                                            onChange={(e) => handleEntryChange(index, 'firm_bank_account_id', e.target.value)}
                                                            required
                                                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                                        >
                                                            <option value="">Select Bank</option>
                                                            {bankAccounts.map(bank => (
                                                                <option key={bank.id} value={bank.id}>{bank.bank_name}</option>
                                                            ))}
                                                        </select>
                                                    );
                                                }
                                                return <span className="text-slate-400 text-xs">-</span>;
                                            })()}
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={entry.debit_amount}
                                                onChange={(e) => handleEntryChange(index, 'debit_amount', e.target.value)}
                                                step="0.01"
                                                min="0"
                                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <input
                                                type="number"
                                                value={entry.credit_amount}
                                                onChange={(e) => handleEntryChange(index, 'credit_amount', e.target.value)}
                                                step="0.01"
                                                min="0"
                                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-right focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeEntry(index)}
                                                className="text-rose-600 hover:text-rose-800"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                                <tr>
                                    <td colSpan="2" className="px-4 py-3 font-bold text-slate-800">Totals</td>
                                    <td className="px-4 py-3 text-right font-bold text-slate-800">₹{totalDebit.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right font-bold text-slate-800">₹{totalCredit.toFixed(2)}</td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="p-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={addEntry}
                            className="text-sm px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                            + Add Entry
                        </button>
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        type="submit"
                        disabled={saving || !balanced}
                        className={`flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors ${(saving || !balanced) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update Voucher' : 'Create Voucher')}
                    </button>
                    <Link
                        to="/accounting/vouchers"
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors text-center"
                    >
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}

export default VoucherForm;
